import { colors } from "@/constants/mapStyles";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { EmeraldInput } from "@emerald-react/input";
import { selectPOIById } from "@features/survey/poisSelectors";
import { updateCategoryField } from "@features/survey/poisSlice";
import { selectCurrentSurveyId } from "@features/survey/surveySelectors";
import { useCurrentPoiId } from "@hooks/useSurveySelectors";
import { surveyPOIApi, useUpdatePOICategoryMutation } from "@services/survey-api";
import React, { Fragment, useState } from "react";

const PoiColorPicker = () => {
  const surveyId = useAppSelector(selectCurrentSurveyId);
  const dispatch = useAppDispatch();
  const currentPoiId = useCurrentPoiId();
  const currentPOI = useAppSelector((state) =>
    selectPOIById(currentPoiId)(state),
  );
  const categoryId = currentPOI?.categoryId || "";
  const [updatePOICategory] = useUpdatePOICategoryMutation();

  const [colorUpdate, setColorUpdate] = useState(
    currentPOI?.category?.iconData?.color || "#a23f97",
  );

  const handleColorPick = (event: React.MouseEvent<HTMLLIElement>) => {
    const pickedColor =
      event.currentTarget.dataset.color ??
      event.currentTarget.style.backgroundColor;
    setColorUpdate(pickedColor);
    
    // 1. Optimistic Redux update
    dispatch(
      updateCategoryField({
        categoryId,
        updates: { iconData: { color: pickedColor } },
      }),
    );
    
    // 2. Optimistic RTK Query cache update
    dispatch(
      surveyPOIApi.util.updateQueryData('getPOICategories', surveyId || "", (draft) => {
        const category = draft.find(c => c.extId === categoryId);
        if (category) {
          category.iconData = category.iconData ?? {};
          category.iconData.color = pickedColor;
        }
      })
    );
    
    // 3. Persist to backend
    updatePOICategory({
      surveyId: surveyId || "",
      categoryId,
      body: { iconData: { color: pickedColor } },
    });
  };

  const handleCustomColorChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const customColor = event.target.value;
    if (customColor.length !== 7) return;
    setColorUpdate(customColor || "#a23f97");
    if (/^#[0-9A-F]{6}$/i.test(customColor)) {
      dispatch(
        updateCategoryField({
          categoryId,
          updates: { iconData: { color: customColor } },
        }),
      );
      
      // Optimistic RTK Query cache update
      dispatch(
        surveyPOIApi.util.updateQueryData('getPOICategories', surveyId || "", (draft) => {
          const category = draft.find(c => c.extId === categoryId);
          if (category) {
            category.iconData = category.iconData ?? {};
            category.iconData.color = customColor;
          }
        })
      );
      
      updatePOICategory({
        surveyId: surveyId || "",
        categoryId,
        body: { iconData: { color: customColor } },
      });
    } else {
      console.error("Invalid hex color code");
    }
  };

  return (
    <div>
      <p>Select a color</p>
      {Object.keys(colors).map((header) => (
        <div key={header} className="flex flex-col gap-2 mr-13 my-2">
          <p className="text-xs">{header}</p>
          <ul className="flex gap-2 flex-wrap">
            {colors[header].map((color: string, index: number) => (
              <Fragment key={`${color}-${index}`}>
                <li
                  onClick={handleColorPick}
                  data-color={color}
                  className="w-6 h-6 rounded-xl min-w-auto cursor-pointer flex"
                  style={{ backgroundColor: color }}
                />
                {index === 4 && <li className="basis-full h-0" />}
              </Fragment>
            ))}
          </ul>
        </div>
      ))}
      <div className="flex flex-col gap-3 py-2">
        <p className="text-xs">Custom Color</p>
        <div className="hexColorInput flex">
          <EmeraldInput
            type="text"
            label="Enter a hex color code"
            value={colorUpdate}
            inputSize="medium"
            isValid={true}
            className="without-label"
            onChange={handleCustomColorChange}
            maxLength={7}
          />
          <p
            className="w-6 h-6 rounded-full m-3"
            style={{ backgroundColor: colorUpdate }}
          />
        </div>
      </div>
    </div>
  );
};

export default PoiColorPicker;
